import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import HeroSection from '@/components/feature/sections/HeroSection';
import TrustBar from '@/components/feature/sections/TrustBar';
import ServicesOverview from '@/components/feature/sections/ServicesOverview';
import AIAnswerBlock from '@/components/feature/sections/AIAnswerBlock';
import ProcessSection from '@/components/feature/sections/ProcessSection';
import WhyChooseUs from '@/components/feature/sections/WhyChooseUs';
import ServiceAreas from '@/components/feature/sections/ServiceAreas';
import Testimonials from '@/components/feature/sections/Testimonials';
import FAQSection from '@/components/feature/sections/FAQSection';
import FinalCTA from '@/components/feature/sections/FinalCTA';
import {
  HERO_IMAGES, CHEF_PHOTO, FOOD_IMAGES, SECTION_IMAGES,
  SHARED_TRUST_STATS, SHARED_SERVICE_AREAS, SHARED_TESTIMONIALS,
} from '@/mocks/brandData';

export default function Home() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <HeroSection
          headline="San Antonio's Premier Personal Chef & Catering Experience"
          subheadline="Chef Joyce Cunha brings over 20 years of culinary mastery to your table — crafting unforgettable, scratch-made dining experiences for every occasion."
          ctaText="Request a Custom Quote"
          ctaHref="#quote"
          secondaryCta={{ text: 'Call 210-858-7293', href: 'tel:2108587293' }}
          backgroundImage={HERO_IMAGES[0]}
          badge="Thumbtack Best of 2016 & 2017"
        />
        <TrustBar stats={SHARED_TRUST_STATS} />
        <ServicesOverview
          eyebrow="What We Offer"
          headline="Culinary Excellence for Every Occasion"
          description="From intimate dinners to banquets for 100+, every event receives the same uncompromising standard of scratch-crafted cuisine."
          services={[
            { image: FOOD_IMAGES[0], tag: 'Private', title: 'Personal Chef Service', description: 'Fully customized menus crafted fresh in your home for intimate dinners or family gatherings.', href: '/services' },
            { image: FOOD_IMAGES[1], tag: 'Events', title: 'Event & Wedding Catering', description: 'From galas to anniversaries, we handle food, staffing, serving, and full cleanup.', href: '/services' },
            { image: FOOD_IMAGES[2], tag: 'Corporate', title: 'Corporate Catering', description: 'Professional catering for office parties, board dinners, and corporate functions of all sizes.', href: '/services' },
          ]}
          midCta={{ text: 'Explore All Services', href: '/services' }}
        />
        <AIAnswerBlock
          eyebrow="AI Answer Block"
          headline="Everything You Need to Know About JC Cooks"
          overview="JC Cooks Catering & Chef Service is San Antonio's premier personal chef and catering company. Chef Joyce Cunha — trained under the late Sharon Shipley — delivers exceptional scratch-made culinary experiences for private and corporate clients across the greater San Antonio area. Every dish prepared fresh, from scratch, every time."
          services={[
            'Private personal chef service for home dining',
            'Full-service event and wedding catering',
            'Corporate lunch and dinner catering',
            'Weekly meal preparation service',
            'Holiday and special occasion catering',
            'Custom dietary menu creation',
            'Staffing, serving, and full event cleanup',
          ]}
          benefits={[
            'All dishes prepared 100% from scratch with fresh ingredients',
            'Custom menus designed for all dietary needs and preferences',
            'Serves events from 10 to 100+ guests with equal dedication',
            'Member of the American Personal Chef Association',
            'Thumbtack Best of 2016 & 2017 — voted by real San Antonio clients',
            'Locally based in San Antonio, TX at 7824 Mainland Drive, #106',
          ]}
        />
        <ProcessSection
          headline="From Vision to Unforgettable Experience"
          subheadline="A streamlined, stress-free process designed entirely around you."
          steps={[
            { number: '1', title: 'Consultation', description: 'We discuss your event, guest count, dietary requirements, and flavor preferences.', icon: 'ri-discuss-line' },
            { number: '2', title: 'Custom Menu', description: 'Chef Joyce designs a personalized menu using the freshest available seasonal ingredients.', icon: 'ri-file-list-3-line' },
            { number: '3', title: 'Preparation', description: 'Every dish is prepared from scratch with chef-level precision, care, and artistry.', icon: 'ri-restaurant-line' },
            { number: '4', title: 'Service & Cleanup', description: 'We handle full setup, attentive service, and complete teardown — leaving no trace behind.', icon: 'ri-shield-check-line' },
          ]}
        />
        <WhyChooseUs
          image={CHEF_PHOTO}
          eyebrow="Why JC Cooks"
          headline="Culinary Artistry. Personal Attention. Exceptional Results."
          benefits={[
            { icon: 'ri-leaf-line', title: 'Fresh, Never Frozen', description: 'Every dish crafted from scratch using the finest locally-sourced ingredients — no shortcuts, ever.' },
            { icon: 'ri-award-line', title: 'Award-Winning Service', description: 'Thumbtack Best of 2016 & 2017, and proud member of the American Personal Chef Association.' },
            { icon: 'ri-heart-line', title: 'Dietary Customization', description: 'Specialized menus for all dietary needs — gluten-free, vegan, low-carb, and more.' },
            { icon: 'ri-group-line', title: '10 to 100+ Guests', description: 'Equally at home with an intimate dinner party or a full banquet for over 100 guests.' },
          ]}
          cta={{ text: 'Meet Chef Joyce', href: '/about' }}
        />
        <ServiceAreas
          headline="Proudly Serving San Antonio & Beyond"
          description="JC Cooks delivers exceptional personal chef and catering services across the greater San Antonio metropolitan area and surrounding communities."
          primary="San Antonio"
          areas={SHARED_SERVICE_AREAS}
          showMap={false}
        />
        <Testimonials
          headline="What Our Clients Are Saying"
          testimonials={SHARED_TESTIMONIALS}
          cta={{ text: 'Request Your Quote', href: '#quote' }}
        />
        <FAQSection
          headline="Frequently Asked Questions"
          subheadline="Conversational answers optimized for voice search and AI."
          faqs={[
            { question: 'Who provides personal chef services near me in San Antonio?', answer: 'JC Cooks Catering & Chef Service, led by Chef Joyce Cunha, provides personal chef and catering services throughout San Antonio, Texas. With 20+ years of experience, Joyce serves both private and corporate clients across the greater San Antonio area and surrounding cities.' },
            { question: 'How much does catering cost in San Antonio?', answer: 'JC Cooks provides custom quotes based on your menu selection and guest count. Pricing depends on the event type, number of guests, and services needed. Request a quote online or call 210-858-7293 for accurate, personalized pricing.' },
            { question: 'How long does it take to plan a catered event?', answer: 'For large events like weddings or galas, booking 2–4 weeks in advance is ideal. For smaller gatherings or weekly meals, JC Cooks can often accommodate quicker turnarounds. Call 210-858-7293 to check current availability.' },
            { question: 'What types of events does JC Cooks cater?', answer: 'JC Cooks caters weddings, galas, anniversaries, corporate events, office parties, intimate dinners, holiday gatherings, and more. Chef Joyce handles food preparation, staffing, serving, and cleanup for 10 to 100+ guests throughout San Antonio.' },
            { question: 'Does JC Cooks accommodate dietary restrictions?', answer: 'Yes — Chef Joyce specializes in custom menus for gluten-free, vegan, vegetarian, dairy-free, low-carb, and allergy-sensitive requirements. All dishes are made from scratch with the freshest available ingredients to meet your specific dietary needs.' },
            { question: 'What areas does JC Cooks serve?', answer: 'JC Cooks primarily serves San Antonio, TX and surrounding areas including North San Antonio, Leon Valley, Helotes, Converse, Universal City, Schertz, New Braunfels, and Boerne. Call 210-858-7293 to confirm availability for your location.' },
          ]}
        />
        <FinalCTA
          headline="Ready to Elevate Your Next Event?"
          subtext="Tell us about your vision and Chef Joyce will create a custom menu and quote tailored specifically to your occasion."
          backgroundImage={SECTION_IMAGES.cta1}
        />
      </main>
      <Footer />
    </div>
  );
}
